"use client";

import * as React from "react";
import { useState } from "react";

import { cn } from "../../lib/utils";

export type VideoEmbedCardProps = {
  src: string;
  title: string;
  className?: string;
  iframeClassName?: string;
  posterSrc?: string;
  playLabel?: string;
  playIconSrc?: string;
  allow?: string;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
};

const DEFAULT_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

export function VideoEmbedCard({
  src,
  title,
  className,
  iframeClassName,
  posterSrc,
  playLabel = "Play video",
  playIconSrc,
  allow = DEFAULT_ALLOW,
  referrerPolicy = "strict-origin-when-cross-origin",
}: VideoEmbedCardProps) {
  const [isPlaying, setIsPlaying] = useState(!posterSrc);

  return (
    <div
      className={cn(
        "w-full aspect-video rounded-xl border border-black overflow-hidden bg-black relative shadow-[4px_4px_0px_0px_#000000]",
        className,
      )}
    >
      {isPlaying ? (
        <iframe
          src={src}
          title={title}
          allow={allow}
          referrerPolicy={referrerPolicy}
          allowFullScreen
          className={cn("w-full h-full border-none", iframeClassName)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setIsPlaying(true)}
          aria-label={playLabel}
          className="group relative flex h-full w-full items-center justify-center overflow-hidden"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-center bg-cover transition-transform duration-300 group-hover:scale-[1.02]"
            style={{ backgroundImage: `url("${posterSrc}")` }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/20"
          />
          {playIconSrc ? (
            <img
              src={playIconSrc}
              alt=""
              aria-hidden="true"
              className="relative z-10 h-12 w-12 object-contain transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="relative z-10 flex h-[56px] w-[56px] items-center justify-center rounded-full border-2 border-black bg-[#fff7ec] shadow-[0px_8px_24px_rgba(0,0,0,0.28)] transition-transform duration-200 group-hover:scale-105">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-white/40 blur-[10px]"
              />
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className="relative z-10 ml-1"
              >
                <path d="M18.5 12L8 18.0622V5.93782L18.5 12Z" fill="#111111" />
              </svg>
            </div>
          )}
          <span className="sr-only">{title}</span>
        </button>
      )}
    </div>
  );
}
